import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { trackEvent } from '../analytics/posthog';
import { getResearchStatus, researchStatusKeys } from '../api/research-rooms';
import { PlusIcon } from '../lib/icons';
import { SessionGrid } from '../sessions/session-grid';
import { DashboardGreeting, DashboardOverview } from './dashboard-overview';
import { EmptyRooms } from './empty-rooms';
import { HomeHeader } from './home-header';
import { useRooms } from './use-rooms';

/**
 * The signed-in home screen: the greeting, "New Room", the room
 * to pick up where you left off, and every room, newest first — or, with
 * none yet (past the first-visit welcome), "No Rooms Yet".
 *
 * There are no projects to pick between — a new room always gets its own
 * link, and the server files it with the other rooms on that site, which is
 * how it starts with the same panel.
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const { captures, loading, panels, rooms } = useRooms();

  /**
   * `?fix=<projectId>` — the link the issue-report extension answers a filed
   * report with. It names the hidden project it filed into; the place to land
   * is that project's most recent room, whose own fix list is in its top bar.
   * With no room to land in (a manual report makes no capture), the link
   * simply stays home — fix lists are per room, never a project-wide panel.
   */
  const [params, setParams] = useSearchParams();
  const fixProjectId = params.get('fix');
  const fixRoomId = fixProjectId
    ? rooms.find((room) => room.projectId === fixProjectId)?.id
    : undefined;

  useEffect(() => {
    if (!fixProjectId || loading) return;
    if (fixRoomId) {
      void navigate(`/sessions/${fixRoomId}`, { replace: true });
      return;
    }
    setParams(
      (current) => {
        current.delete('fix');
        return current;
      },
      { replace: true },
    );
  }, [fixProjectId, fixRoomId, loading, navigate, setParams]);

  const { data: statuses = [] } = useQuery({
    queryFn: () => getResearchStatus(),
    queryKey: researchStatusKeys.everywhere(),
  });

  // Nothing paints while a `?fix` redirect is in flight — a grid about to be
  // navigated away from is a frame that should never have been shown.
  if (fixProjectId && (loading || fixRoomId)) {
    return <main className="min-h-svh bg-warm" />;
  }

  const empty = !loading && rooms.length === 0;

  return (
    <main className="min-h-svh bg-surface">
      <div className="px-4 pt-5 lg:px-10">
        <HomeHeader />
      </div>

      <div className="mx-auto w-full max-w-[1160px] px-6 pt-11 pb-16 xl:px-0">
        <div className="flex flex-wrap items-end justify-between gap-x-11 gap-y-5">
          <div className="min-w-0">
            <DashboardGreeting empty={empty} statuses={statuses} />
          </div>
          {empty ? null : (
            <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
              <button
                className="flex h-12 cursor-pointer items-center gap-2 rounded-button bg-brand pr-[22px] pl-[18px] text-ui-lg font-bold text-white transition hover:bg-brand-alt active:scale-[0.98]"
                onClick={() => {
                  trackEvent('new_room_clicked', { from: 'home' });
                  void navigate('/new');
                }}
                type="button"
              >
                <PlusIcon aria-hidden="true" className="size-4" />
                New Room
              </button>
            </div>
          )}
        </div>

        {empty ? (
          <EmptyRooms />
        ) : (
          <>
            <DashboardOverview
              captures={captures}
              rooms={rooms}
              statuses={statuses}
            />

            <h2 className="!mt-8 !mb-0 !text-panel-title !font-bold !tracking-tight !text-text-primary">
              All Rooms
            </h2>
            {loading ? (
              <ul className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {/* Skeletons match the card's shape, so nothing shifts when
                    the real ones arrive. */}
                {[0, 1, 2].map((key) => (
                  <li
                    className="overflow-hidden rounded-room bg-surface p-2.5 shadow-room"
                    key={key}
                  >
                    <div className="aspect-[350/176] animate-pulse rounded-button bg-violet-soft motion-reduce:animate-none" />
                    <div className="space-y-2 px-2.5 py-4">
                      <div className="h-4 w-1/2 animate-pulse rounded-pill bg-hairline motion-reduce:animate-none" />
                      <div className="h-3 w-3/4 animate-pulse rounded-pill bg-hairline-alt motion-reduce:animate-none" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <SessionGrid
                captures={captures}
                panels={panels}
                sessions={rooms}
                statuses={statuses}
              />
            )}
          </>
        )}
      </div>
    </main>
  );
}
